import { CONSOLE, FINALE, SETUP, STATEMENT } from "@/content/landing";
import { COMPANY } from "@/lib/company";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const CONTAINER = "mx-auto w-full max-w-6xl px-4 sm:px-6";
const GRID = "grid md:grid-cols-12 md:gap-x-8";

/** 컨시어지가 답을 어디서 가져오는지 — 큰 문단 하나. */
export function StatementSection() {
  return (
    <section aria-labelledby="statement-title" className={`${CONTAINER} py-28 md:py-44`}>
      <div className={GRID}>
        <h2 id="statement-title" className="sl-xl md:col-span-10" data-split>
          {STATEMENT.title}
        </h2>
        <p
          className="sl-l mt-8 font-semibold md:col-span-9 md:mt-12"
          data-split
          data-delay="0.1"
        >
          {STATEMENT.body}
        </p>
      </div>
    </section>
  );
}

/** 운영자 콘솔의 기능 — 카드 대신 전폭 인덱스 행. */
export function ConsoleSection() {
  return (
    <section aria-labelledby="console-title" className={`${CONTAINER} pb-28 md:pb-40`}>
      <div className={`${GRID} items-end`}>
        <h2 id="console-title" className="sl-xl md:col-span-7" data-split>
          {CONSOLE.title}
        </h2>
        <p className="sl-body sl-muted mt-6 md:col-span-4 md:col-start-9 md:mt-0">{CONSOLE.lede}</p>
      </div>
      {/* 스토리의 선이 이어진다: 각 기능은 체류의 어느 시점에 쓰이는지로 줄을 선다. */}
      <ul className="relative mt-14 md:mt-20">
        <li
          aria-hidden="true"
          className="absolute bottom-0 left-[5px] top-0 w-px bg-current opacity-[0.16]"
        />
        {CONSOLE.rows.map((row) => (
          <li key={row.name} className={`${GRID} relative py-7 pl-9 md:py-9 md:pl-0`}>
            <span
              aria-hidden="true"
              className="sl-node absolute left-0 top-[2.15rem] md:top-[2.65rem]"
            />
            <p className="sl-time text-[13px] font-semibold md:col-span-3 md:col-start-1 md:pl-9 md:pt-1.5">
              {row.when}
            </p>
            <h3 className="sl-m mt-2 md:col-span-4 md:mt-0">{row.name}</h3>
            <p className="sl-body sl-muted mt-2 md:col-span-5 md:col-start-8 md:mt-0">{row.body}</p>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** 시작까지의 세 단계 — 실제 순서라 번호를 쓴다. 가로선 위의 세 정거장. */
export function SetupSection() {
  return (
    <section aria-labelledby="setup-title" className={`${CONTAINER} pb-28 md:pb-40`}>
      <h2 id="setup-title" className="sl-xl" data-split>
        {SETUP.title}
      </h2>
      <ol className="relative mt-14 grid gap-12 md:mt-20 md:grid-cols-3 md:gap-x-8">
        <li
          aria-hidden="true"
          className="sl-rule absolute inset-x-0 top-[5px] hidden md:block"
          data-draw
        />
        <li
          aria-hidden="true"
          className="absolute bottom-2 left-[5px] top-2 w-px bg-current opacity-[0.14] md:hidden"
        />
        {SETUP.steps.map((step, i) => (
          <li key={step.title} className="relative pl-9 md:pl-0 md:pt-12">
            <span aria-hidden="true" className="sl-node absolute left-0 top-1 md:top-0" />
            <p className="sl-time text-[13px] font-semibold">{String(i + 1).padStart(2, "0")}</p>
            <h3 className="sl-m mt-3">{step.title}</h3>
            <p className="sl-body sl-muted mt-3 max-w-[24rem]">{step.body}</p>
          </li>
        ))}
      </ol>
    </section>
  );
}

/** 마지막 장면: 제휴 문의 메일 주소를 디스플레이 크기로. 체류의 선은 여기서 다음 예약으로 이어진다. */
export function FinaleSection({
  consoleTo,
  consoleCta,
}: { consoleTo: string; consoleCta: string }) {
  return (
    <section aria-labelledby="finale-title" className={`${CONTAINER} pb-24 md:pb-32`}>
      <div className="relative pt-16 md:pt-24">
        <div className="absolute inset-x-0 top-0 flex items-center" aria-hidden="true">
          <div className="sl-rule flex-1" data-draw />
          <span className="ml-2 inline-block h-[11px] w-[11px] rounded-full bg-[var(--sl-accent)]" />
        </div>
        <h2 id="finale-title" className="sl-m">
          {FINALE.title}
        </h2>
        <div className="mt-5 [container-type:inline-size]">
          <a href={`mailto:${COMPANY.email}`} className="sl-link sl-finale-mail">
            {COMPANY.email}
          </a>
        </div>
        <div className={`${GRID} mt-12 gap-y-10 md:mt-16`}>
          <p className="sl-body sl-muted max-w-[30rem] md:col-span-5">{FINALE.body}</p>
          <div className="flex flex-col items-start gap-5 md:col-span-5 md:col-start-8">
            <div className="flex flex-wrap gap-3">
              <Link to={consoleTo} className="sl-btn">
                {consoleCta}
                <ArrowRight aria-hidden className="sl-btn__arrow h-4 w-4" />
              </Link>
              <Link to={FINALE.secondaryCta.to} className="sl-btn sl-btn--ghost">
                {FINALE.secondaryCta.label}
              </Link>
            </div>
            <p className="sl-muted text-[13px] leading-relaxed">{FINALE.betaNote}</p>
          </div>
        </div>
      </div>
    </section>
  );
}
